import { db } from "@/services/config";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
  startAt,
  endAt,
  orderBy,
  DocumentData,
  documentId,
  serverTimestamp,
} from "firebase/firestore";
import { VisitorRecord } from "./feed.types";
import { blockService } from "@/services/usersBlocked/usersBlockedService";
import * as geofire from "geofire-common";
import { getOppositeGender, slugify } from "@/utils";

export class FeedService {
  private collectionUserRef = collection(db, "users");
  private collectionVisitorRef = collection(db, "visitors");

  private toLatLng(
    geopoint:
      | { latitude?: number; longitude?: number; lat?: number; lng?: number }
      | undefined
      | null,
  ): { lat: number; lng: number } | null {
    if (!geopoint) return null;
    const lat =
      typeof geopoint.latitude === "number"
        ? geopoint.latitude
        : typeof geopoint.lat === "number"
          ? geopoint.lat
          : null;
    const lng =
      typeof geopoint.longitude === "number"
        ? geopoint.longitude
        : typeof geopoint.lng === "number"
          ? geopoint.lng
          : null;
    if (lat === null || lng === null) return null;
    return { lat, lng };
  }

  async getDiscoveryFeed(params: {
    uid: string;
    gender: string;
    pageSize: number;
    userLocation?: {
      lat?: number;
      lng?: number;
      latitude?: number;
      longitude?: number;
    };
    excludeIds?: string[];
    distanceKm?: number;
  }): Promise<{ profiles: any[]; lastDoc: null }> {
    try {
      const { uid, gender, pageSize, userLocation, excludeIds, distanceKm } =
        params;

      const [blockedIdsArray] = await Promise.all([
        blockService.listBlockedIds(uid),
      ]);
      const blockedIds = new Set(blockedIdsArray);
      const alreadyLoadedIds = new Set(excludeIds || []);
      const genderToSearch = getOppositeGender(gender);

      const centerLatLng = this.toLatLng(userLocation);
      const results: any[] = [];

      // 1. BUSCA POR LOCALIZAÇÃO (GEOHASH)
      if (centerLatLng?.lat && centerLatLng?.lng) {
        const centerPoint: [number, number] = [
          centerLatLng.lat,
          centerLatLng.lng,
        ];
        const radiusInMeters = (distanceKm || 50) * 1000;

        const bounds = geofire.geohashQueryBounds(centerPoint, radiusInMeters);
        const promises = [];

        const geoLimit = Math.max(pageSize * 3, 50);

        for (const b of bounds) {
          const q = query(
            this.collectionUserRef,
            where("closeAccount", "==", false),
            where("awaitingApproval", "==", false),
            where("visible", "==", true),
            where("gender", "in", genderToSearch),
            orderBy("location.geohash"),
            startAt(b[0]),
            endAt(b[1]),
            limit(geoLimit),
          );
          promises.push(getDocs(q));
        }

        const snapshots = await Promise.all(promises);

        for (const snapshot of snapshots) {
          for (const doc of snapshot.docs) {
            if (
              doc.id === uid ||
              blockedIds.has(doc.id) ||
              alreadyLoadedIds.has(doc.id)
            ) {
              continue;
            }

            const data = doc.data();

            if (data.location?.geopoint) {
              const targetLatLng = this.toLatLng(data.location.geopoint);

              if (targetLatLng) {
                const distanceInKm = geofire.distanceBetween(centerPoint, [
                  targetLatLng.lat,
                  targetLatLng.lng,
                ]);

                if (distanceInKm <= (distanceKm || 50)) {
                  const approvedPhotos =
                    data.publicPhotos?.flatMap((photo: any) => {
                      if (typeof photo === "string") return [photo];
                      if (photo?.approved && photo?.url) return [photo.url];
                      return [];
                    }) || [];

                  results.push({
                    uid: doc.id,
                    username: data.username,
                    usernameSlug: slugify(data.username),
                    age: data.age || 0,
                    city: data.city || "",
                    state: data.state || "",
                    gender: data.gender,
                    profileImage: data.profileImage,
                    birthYear: data.birthYear,
                    photosCount: approvedPhotos.length,
                    distanceInKm: parseFloat(distanceInKm.toFixed(1)),
                    isVerified: data.isVerified ?? false,
                    planInfo: data.planInfo || null,
                  });
                }
              }
            }
          }
        }

        // Ordena os locais por distância
        results.sort((a, b) => a.distanceInKm - b.distanceInKm);
      }

      // 2. SMART FALLBACK SE NÃO HOUVER RESULTADOS SUFICIENTES
      // (Seja porque o usuário não tem localização ou porque encontramos menos que o pageSize)
      if (results.length < pageSize) {
        const currentBatchIds = new Set([
          ...alreadyLoadedIds,
          ...results.map((r) => r.uid),
        ]);

        const fallbackLimit = Math.max(pageSize * 4, 80);

        const q = query(
          this.collectionUserRef,
          where("closeAccount", "==", false),
          where("awaitingApproval", "==", false),
          where("visible", "==", true),
          where("gender", "in", genderToSearch),
          where(documentId(), "!=", uid),
          orderBy("createdAt", "desc"),
          limit(fallbackLimit),
        );

        const snapshot = await getDocs(q);

        for (const doc of snapshot.docs) {
          // Evita duplicar o que veio no Geohash ou o que já estava nas páginas anteriores
          if (blockedIds.has(doc.id) || currentBatchIds.has(doc.id)) continue;

          const data = doc.data();

          const approvedPhotos =
            data.publicPhotos?.flatMap((photo: any) => {
              if (typeof photo === "string") return [photo];
              if (photo?.approved && photo?.url) return [photo.url];
              return [];
            }) || [];

          results.push({
            uid: doc.id,
            username: data.username,
            usernameSlug: slugify(data.username),
            age: data.age || 0,
            city: data.city || "",
            state: data.state || "",
            gender: data.gender,
            profileImage: data.profileImage,
            birthYear: data.birthYear,
            photosCount: approvedPhotos.length,
            distanceInKm: null,
            isVerified: data.isVerified ?? false,
            planInfo: data.planInfo || null,
          });

          // Se já preenchemos o tamanho da página solicitado, podemos otimizar o loop
          if (results.length >= pageSize) {
            break;
          }
        }
      }

      return {
        profiles: results.slice(0, pageSize),
        lastDoc: null,
      };
    } catch (error) {
      console.error("[getDiscoveryFeed] Erro crítico:", error);
      return { profiles: [], lastDoc: null };
    }
  }

  async getProfileByUsername(identifier: string, filters: any) {
    try {
      let uid: string | null = null;
      let userData: any = null;

      // ==================================================
      // Tenta buscar por index direto
      // ==================================================

      const usernameIndexRef = doc(db, "usernames", identifier);

      const usernameIndexSnap = await getDoc(usernameIndexRef);

      if (usernameIndexSnap.exists()) {
        uid = usernameIndexSnap.data().uid;
      }

      // ==================================================
      // Fallback username + slug em paralelo
      // ==================================================

      if (!uid) {
        const [usernameSnap, slugSnap] = await Promise.all([
          getDocs(
            query(
              collection(db, "users"),
              where("username", "==", identifier),
              limit(1),
            ),
          ),

          getDocs(
            query(
              collection(db, "users"),
              where("usernameSlug", "==", identifier),
              limit(1),
            ),
          ),
        ]);

        if (!usernameSnap.empty) {
          uid = usernameSnap.docs[0].id;
          userData = usernameSnap.docs[0].data();
        } else if (!slugSnap.empty) {
          uid = slugSnap.docs[0].id;
          userData = slugSnap.docs[0].data();
        }
      }

      if (!uid) {
        return null;
      }

      // ==================================================
      // Carrega usuário
      // ==================================================

      if (!userData) {
        const userDoc = await getDoc(doc(db, "users", uid));

        if (!userDoc.exists()) {
          return null;
        }

        userData = userDoc.data();
      }

      const viewer = filters?.viewer;
      const isSameUser = viewer?.uid === uid;

      if (!isSameUser) {
        if (userData.closeAccount) {
          return null;
        }

        const viewerGender = viewer?.gender?.toLowerCase()?.trim();

        const targetGender = userData?.gender?.toLowerCase()?.trim();

        if (viewerGender && targetGender && viewerGender === targetGender) {
          return null;
        }

        if (viewer?.uid) {
          const blocked = await blockService.isBlocked(viewer.uid, uid);

          if (blocked) {
            return null;
          }
        }
      }

      // ==================================================
      // Indexação assíncrona (não bloqueia)
      // ==================================================

      if (!usernameIndexSnap.exists()) {
        setDoc(usernameIndexRef, {
          uid,
          username: userData.username,
          updatedAt: serverTimestamp(),
        }).catch(console.error);
      }

      // ==================================================
      // Fotos aprovadas
      // ==================================================

      const approvedPhotos =
        userData.publicPhotos?.flatMap((photo: any) => {
          if (typeof photo === "string") {
            return [photo];
          }

          if (photo?.approved && photo?.url) {
            return [photo.url];
          }

          return [];
        }) || [];

      return {
        id: uid,
        ...userData,
        usernameSlug: userData.usernameSlug || identifier,
        photosCount: approvedPhotos.length,
        publicPhotos: approvedPhotos,
      };
    } catch (error) {
      console.error("[getProfileByUsername]", error);

      throw error;
    }
  }

  /**
   * Lista os usuários que visitaram o meu perfil (onde eu sou o targetId)
   */
  async getVisitors(uid: string): Promise<VisitorRecord[]> {
    const q = query(
      this.collectionVisitorRef,
      where("targetId", "==", uid),
      orderBy("createdAt", "desc"),
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as VisitorRecord[];
  }

  async registerView(
    viewerId: string,
    targetId: string,
    viewerData: VisitorRecord["viewerData"],
  ) {
    if (viewerId === targetId) return;

    // ID composto robusto: visitanteUID_alvoUID
    const visitorDocId = `${viewerId}_${targetId}`;
    const docRef = doc(this.collectionVisitorRef, visitorDocId);

    try {
      await setDoc(
        docRef,
        {
          viewerId,
          targetId,
          viewerData,
          createdAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Erro crítico ao gravar visitante no Firestore:", error);
    }
  }
}

export const feedService = new FeedService();
